import { useSuspenseQuery } from "@apollo/client/react";
import { Loader2 } from "lucide-react";
import { Suspense, useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAdminData } from "~/common/contexts";
import { DetailLoading } from "~/components/loading";
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  MultiSelect,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "~/components/ui";
import {
  GetOrganizationsForUserSelectDocument,
  type GetOrganizationsForUserSelectQuery,
} from "~/features/admin/users/gqls";
import { useUserCreate, useUserEdit, useUserForUpdate } from "~/features/admin/users/hooks";
import type { UserEditFormData, UserFormData } from "~/features/admin/users/types";
import { emailPattern } from "~/utils/patterns";
import { ProductRoleAssignmentsField } from "./ProductRoleAssignmentsField";
import { UserEmailChange } from "./UserEmailChange";

interface UserFormDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /** If set, the dialog is in edit mode and prefills from this user id. */
  userId?: string;
}

const FORM_ID = "user-form";
const NONE = "__none";

export function UserFormDialog({ open, onOpenChange, userId }: UserFormDialogProps) {
  const { t } = useTranslation();
  const isEdit = Boolean(userId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="capitalize">
            {isEdit ? `${t("ui.edit")} ${t("model.user.modelName")}` : `${t("ui.add")} ${t("model.user.modelName")}`}
          </DialogTitle>
        </DialogHeader>
        <Suspense fallback={<DetailLoading />}>
          {isEdit && userId ? (
            <EditBody userId={userId} onClose={() => onOpenChange(false)} />
          ) : (
            <CreateBody onClose={() => onOpenChange(false)} />
          )}
        </Suspense>
      </DialogContent>
    </Dialog>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <span className="text-12-medium text-fg-2">{children}</span>;
}

function FormFooter({ processing }: { processing: boolean }) {
  const { t } = useTranslation();
  return (
    <DialogFooter>
      <DialogClose asChild>
        <Button type="button" variant="outline" disabled={processing}>
          {t("ui.cancel")}
        </Button>
      </DialogClose>
      <Button type="submit" form={FORM_ID} disabled={processing}>
        {processing && <Loader2 className="animate-spin" />}
        <span>{t("ui.save")}</span>
      </Button>
    </DialogFooter>
  );
}

function CreateBody({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const { products, userSegments, userGroups } = useAdminData();
  const { data: organizationsData } = useSuspenseQuery<GetOrganizationsForUserSelectQuery>(
    GetOrganizationsForUserSelectDocument,
  );
  const organizations = organizationsData.organizations;
  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<UserFormData>({
    defaultValues: {
      displayName: "",
      email: "",
      memo: "",
      organizationShortCode: "",
      userSegmentIds: [],
      userGroupIds: [],
    },
  });

  const { submit, processing, selectedRoles, setRoleForProduct } = useUserCreate({
    onSuccess: () => {
      reset();
      onClose();
    },
  });

  const productsWithRoles = useMemo(
    () => products.filter((p) => p.productRoles && p.productRoles.length > 0),
    [products],
  );

  return (
    <>
      <div className="max-h-[65vh] space-y-4 overflow-y-auto pr-1">
        <form id={FORM_ID} onSubmit={handleSubmit(submit)} className="space-y-4">
          <div className="space-y-1.5">
            <FieldLabel>{t("model.user.displayName")}</FieldLabel>
            <Input {...register("displayName")} />
          </div>

          <div className="flex flex-col gap-1.5">
            <FieldLabel>{t("model.user.email")}</FieldLabel>
            <Input
              {...register("email", {
                required: t("ui.required"),
                pattern: { value: emailPattern, message: t("feature.validation.pattern") },
              })}
              aria-invalid={Boolean(errors.email)}
            />
            {errors.email && <p className="text-12-medium text-danger">{errors.email.message}</p>}
          </div>

          <div className="space-y-1.5">
            <FieldLabel>{t("model.user.organization")}</FieldLabel>
            <Controller
              name="organizationShortCode"
              control={control}
              rules={{ required: t("ui.required") }}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger className="h-9" aria-invalid={Boolean(errors.organizationShortCode)}>
                    <SelectValue placeholder={t("feature.users.selectOrganization")} />
                  </SelectTrigger>
                  <SelectContent>
                    {organizations.map((organization) => (
                      <SelectItem key={organization.id} value={organization.shortCode}>
                        {organization.name} ({organization.shortCode})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {errors.organizationShortCode && (
              <p className="text-12-medium text-danger">{errors.organizationShortCode.message}</p>
            )}
          </div>

          {productsWithRoles.map((product) => {
            const currentRoleId = selectedRoles.get(product.id)?.roleId ?? "";
            return (
              <div key={product.id} className="space-y-1.5">
                <FieldLabel>{product.name}</FieldLabel>
                <Select
                  value={currentRoleId || NONE}
                  onValueChange={(value) => setRoleForProduct(product.id, value === NONE ? null : value)}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>{t("ui.none")}</SelectItem>
                    {product.productRoles.map(({ role }) => (
                      <SelectItem key={role.id} value={role.id}>
                        {role.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            );
          })}

          <div className="space-y-1.5">
            <FieldLabel>{t("model.user.segments")}</FieldLabel>
            <Controller
              name="userSegmentIds"
              control={control}
              render={({ field }) => (
                <MultiSelect<number>
                  label={t("model.user.segments")}
                  options={userSegments.map((s) => ({ value: s.id, label: s.name }))}
                  selected={field.value}
                  onChange={field.onChange}
                  className="w-full"
                />
              )}
            />
          </div>

          <div className="space-y-1.5">
            <FieldLabel>{t("model.user.groups")}</FieldLabel>
            <Controller
              name="userGroupIds"
              control={control}
              render={({ field }) => (
                <MultiSelect<string>
                  label={t("model.user.groups")}
                  options={userGroups.map((g) => ({ value: g.id, label: g.name }))}
                  selected={field.value}
                  onChange={field.onChange}
                  className="w-full"
                />
              )}
            />
          </div>

          <div className="space-y-1.5">
            <FieldLabel>{t("model.user.memo")}</FieldLabel>
            <Textarea {...register("memo")} rows={3} />
          </div>
        </form>
      </div>
      <FormFooter processing={processing} />
    </>
  );
}

function EditBody({ userId, onClose }: { userId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const { products, userSegments, userGroups } = useAdminData();
  const { data } = useUserForUpdate(userId);
  const user = data.usersByPk;

  const initialRoles = useMemo(
    () => user?.userProductRoles.map((upr) => ({ productId: upr.productId, roleId: upr.roleId })) ?? undefined,
    [user],
  );

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors },
  } = useForm<UserEditFormData>({
    defaultValues: {
      id: userId,
      displayName: user?.displayName ?? "",
      email: user?.email ?? "",
      memo: user?.memo ?? "",
      userSegmentIds: user?.userUserSegments.map((uus) => uus.userSegmentId) ?? [],
      userGroupIds: user?.userUserGroups?.map((uug) => uug.userGroupId) ?? [],
    },
  });

  useEffect(() => {
    if (user) setValue("email", user.email);
  }, [user, setValue]);

  const { submit, processing, selectedRoles, setRoleForProduct } = useUserEdit({
    initialRoles,
    onSuccess: () => {
      reset();
      onClose();
    },
  });

  const productsWithRoles = useMemo(
    () => products.filter((p) => p.productRoles && p.productRoles.length > 0),
    [products],
  );

  if (!user) {
    return <div className="py-6 text-center text-14-regular text-fg-3">—</div>;
  }

  return (
    <>
      <div className="max-h-[65vh] space-y-4 overflow-y-auto pr-1">
        <form
          id={FORM_ID}
          onSubmit={handleSubmit((formData) => submit({ ...formData, id: userId }))}
          className="space-y-4"
        >
          <div className="space-y-1.5">
            <FieldLabel>{t("model.user.displayName")}</FieldLabel>
            <Input {...register("displayName")} />
          </div>

          <div className="flex flex-col gap-1.5">
            <FieldLabel>{t("model.user.email")}</FieldLabel>
            <Input
              readOnly
              {...register("email", {
                required: t("ui.required"),
                pattern: { value: emailPattern, message: t("feature.validation.pattern") },
              })}
              aria-invalid={Boolean(errors.email)}
            />
            {errors.email && <p className="text-12-medium text-danger">{errors.email.message}</p>}
            <UserEmailChange userId={userId} currentEmail={user.email} />
          </div>

          <div className="space-y-1.5">
            <FieldLabel>{t("feature.users.addProductRole")}</FieldLabel>
            <ProductRoleAssignmentsField
              products={productsWithRoles}
              selectedRoles={selectedRoles}
              onChange={setRoleForProduct}
            />
          </div>

          <div className="space-y-1.5">
            <FieldLabel>{t("model.user.segments")}</FieldLabel>
            <Controller
              name="userSegmentIds"
              control={control}
              render={({ field }) => (
                <MultiSelect<number>
                  label={t("model.user.segments")}
                  options={userSegments.map((s) => ({ value: s.id, label: s.name }))}
                  selected={field.value}
                  onChange={field.onChange}
                  className="w-full"
                />
              )}
            />
          </div>

          <div className="space-y-1.5">
            <FieldLabel>{t("model.user.groups")}</FieldLabel>
            <Controller
              name="userGroupIds"
              control={control}
              render={({ field }) => (
                <MultiSelect<string>
                  label={t("model.user.groups")}
                  options={userGroups.map((g) => ({ value: g.id, label: g.name }))}
                  selected={field.value}
                  onChange={field.onChange}
                  className="w-full"
                />
              )}
            />
          </div>

          <div className="space-y-1.5">
            <FieldLabel>{t("model.user.memo")}</FieldLabel>
            <Textarea {...register("memo")} rows={3} />
          </div>
        </form>
      </div>
      <FormFooter processing={processing} />
    </>
  );
}
